import React from "react";
import { AppleLoginWidget } from "../../components/AppleLoginWidget/AppleLoginWidget";
import Button, { ButtonDesign } from "../../components/Button/Button";
import { GoogleLoginWidget } from "../../components/GoogleLoginWidget/GoogleLoginWidget";
import Input from "../../components/Input/Input";
import { Layout } from "../../components/Layout/Layout";
import { TelegramLoginWidget } from "../../components/TelegramLoginWidget/TelegramLoginWidget";
import { view } from "@orderofchaos/mvvm";
import { AccountPageViewModel } from "./AccountPageViewModel";
import styles from "./AccountPage.module.css";

export const AccountPage: React.FC = view(AccountPageViewModel)(({
  viewModel: {
    user,
    isAdmin,
    pageSubtitle,
    identities,
    sortedIdentities,
    hasEmailIdentity,
    profileNameInput,
    profileEmailInput,
    profilePhotoInput,
    isLoading,
    isSavingProfile,
    isLinkingEmail,
    isLinkingTelegram,
    isLinkingGoogle,
    isLinkingApple,
    isTelegramMiniApp,
    telegramBotUsername,
    googleClientId,
    appleClientId,
    appleRedirectUri,
    canUseTelegramWidget,
    canUseGoogleWidget,
    canUseAppleWidget,
    canLinkTelegramMiniApp,
    error,
    success,
    hasProfileChanges,
    canSaveProfile,
    canSaveEmail,
    refresh,
    onProfileNameInputChange,
    onProfilePhotoInputChange,
    onEmailInputChange,
    saveProfile,
    saveEmail,
    linkTelegram,
    linkTelegramFromWidget,
    linkGoogleFromCredential,
    linkAppleFromIdToken,
    onProviderAuthError,
    unlinkIdentity,
    isProviderProcessing,
    providerLabel,
  },
}) => {
  return (
    <Layout title={"Аккаунт"}>
      <section className={styles.root}>
        <div className={styles.toolbar}>
          <div className={styles.titleBlock}>
            <strong className={styles.title}>Профиль и способы входа</strong>
            <span className={styles.subtitle}>{pageSubtitle}</span>
          </div>
          <Button
            design={ButtonDesign.ghost}
            className={styles.refresh}
            onClick={() => void refresh()}
            disabled={isLoading}
          >
            обновить
          </Button>
        </div>

        <article className={styles.profileCard}>
          <div className={styles.profileMeta}>
            <div className={styles.profileLine}>
              <span className={styles.profileLabel}>роль:</span>
              <span>{user?.role || "user"}</span>
            </div>
          </div>

          <div className={styles.profileForm}>
            <div className={styles.field}>
              <span className={styles.profileLabel}>имя</span>
              <Input
                className={styles.input}
                placeholder="Имя пользователя"
                value={profileNameInput}
                onChange={onProfileNameInputChange}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.profileLabel}>аватар (URL)</span>
              <Input
                className={styles.input}
                placeholder="https://example.com/avatar.png"
                value={profilePhotoInput}
                onChange={onProfilePhotoInputChange}
              />
            </div>
          </div>

          <div className={styles.actions}>
            <Button
              design={ButtonDesign.basic}
              className={styles.actionButton}
              onClick={() => void saveProfile()}
              disabled={!canSaveProfile}
              isLoading={isSavingProfile}
            >
              {hasProfileChanges ? "сохранить профиль" : "профиль сохранен"}
            </Button>
          </div>
        </article>

        {error && <div className={styles.error}>{error}</div>}
        {success && <div className={styles.success}>{success}</div>}

        <article className={styles.block}>
          <div className={styles.blockHeader}>
            <strong>Привязанные входы</strong>
            <span className={styles.count}>{identities.length}</span>
          </div>

          {isLoading ? (
            <div className={styles.emptyState}>Загружаем привязки...</div>
          ) : sortedIdentities.length === 0 ? (
            <div className={styles.emptyState}>Привязок пока нет</div>
          ) : (
            <ul className={styles.identityList}>
              {sortedIdentities.map((identity) => {
                const processing = isProviderProcessing(identity.provider);
                return (
                  <li key={`${identity.provider}_${identity.providerUserId}`}>
                    <article className={styles.identityCard}>
                      <div className={styles.identityMeta}>
                        <strong>{providerLabel(identity.provider)}</strong>
                        <span>
                          {identity.providerLogin || identity.providerUserId}
                        </span>
                      </div>
                      <Button
                        design={ButtonDesign.basic}
                        className={styles.unlinkButton}
                        disabled={processing || isLoading}
                        isLoading={processing}
                        onClick={() => void unlinkIdentity(identity.provider)}
                      >
                        отвязать
                      </Button>
                    </article>
                  </li>
                );
              })}
            </ul>
          )}
        </article>

        <article className={styles.block}>
          <div className={styles.blockHeader}>
            <strong>Email аккаунта</strong>
            {hasEmailIdentity ? (
              <span className={styles.statusBadge}>уже привязан</span>
            ) : null}
          </div>
          <div className={styles.row}>
            <Input
              className={styles.input}
              placeholder="email@example.com"
              value={profileEmailInput}
              onChange={onEmailInputChange}
            />
            <Button
              design={ButtonDesign.basic}
              className={styles.actionButton}
              onClick={() => void saveEmail()}
              disabled={!canSaveEmail}
              isLoading={isLinkingEmail}
            >
              сохранить email
            </Button>
          </div>
        </article>

        {isAdmin ? (
          <article className={styles.block}>
            <div className={styles.blockHeader}>
              <strong>Привязать Google / Apple / Telegram</strong>
            </div>
            {!isTelegramMiniApp && (
              <div className={styles.socialProviders}>
                {googleClientId ? (
                  <GoogleLoginWidget
                    className={styles.socialWidget}
                    clientId={googleClientId}
                    onCredential={(token) =>
                      void linkGoogleFromCredential(token)
                    }
                    onError={onProviderAuthError}
                    disabled={!canUseGoogleWidget || isLoading}
                    text={"continue_with"}
                    width={320}
                  />
                ) : (
                  <p className={styles.hint}>
                    Укажите `VITE_GOOGLE_CLIENT_ID`, чтобы включить вход через
                    Google.
                  </p>
                )}

                {appleClientId ? (
                  <AppleLoginWidget
                    className={styles.appleWidget}
                    clientId={appleClientId}
                    redirectUri={appleRedirectUri}
                    onIdToken={(token) => void linkAppleFromIdToken(token)}
                    onError={onProviderAuthError}
                    disabled={!canUseAppleWidget || isLoading}
                    label={isLinkingApple ? "Apple..." : "Sign in with Apple"}
                  />
                ) : (
                  <p className={styles.hint}>
                    Укажите `VITE_APPLE_CLIENT_ID`, чтобы включить вход через
                    Apple.
                  </p>
                )}
              </div>
            )}

            {isTelegramMiniApp ? (
              <>
                <p className={styles.hint}>
                  Привязка выполнится через данные Telegram Mini App
                  (`initData`) без ручного ввода.
                </p>
                <Button
                  design={ButtonDesign.basic}
                  className={styles.actionButton}
                  onClick={() => void linkTelegram()}
                  disabled={!canLinkTelegramMiniApp}
                  isLoading={
                    isLinkingTelegram || isLinkingGoogle || isLinkingApple
                  }
                >
                  привязать Telegram
                </Button>
              </>
            ) : telegramBotUsername ? (
              <>
                <p className={styles.hint}>
                  Используйте официальный Telegram Login Widget для привязки
                  Telegram-профиля.
                </p>
                <TelegramLoginWidget
                  className={styles.telegramWidget}
                  botUsername={telegramBotUsername}
                  onAuth={(payload) => void linkTelegramFromWidget(payload)}
                  disabled={!canUseTelegramWidget || isLoading}
                  size={"large"}
                  cornerRadius={8}
                  requestAccess={"write"}
                />
                {isLinkingTelegram ? (
                  <span className={styles.hint}>Выполняем привязку...</span>
                ) : null}
              </>
            ) : (
              <p className={styles.hint}>
                Укажите `VITE_TELEGRAM_BOT_USERNAME`, чтобы включить официальный
                Telegram Login Widget.
              </p>
            )}
          </article>
        ) : null}
      </section>
    </Layout>
  );
});
